// multiRecordContext.js — the MultiRecord++ sliding window.
//
// Owns ONE thing: the tail of the transcript the backend has already attributed,
// so the next chunk is segmented by reading the conversation instead of a single
// carried-over patient name (which is all `previousLastSpeaker` ever gave the
// classic path, and which got reset at exactly the handoff where it mattered).
//
// Deliberately NOT a hook: no rendering, no network, no localStorage. The
// recorder hands it backend responses and asks it for the next payload; the
// caller decides where a snapshot gets persisted. That keeps it testable with
// plain assertions instead of a React harness.
//
// The BACKEND owns attribution. This never invents or edits a tag — it only
// accumulates what came back.

// Mirrors SEGMENT_CONTEXT_CHAR_CAP in the backend segmenter. The backend trims
// again on arrival (it cannot trust a client), so this cap is about keeping the
// request small, not about safety.
export const CONTEXT_CHAR_CAP = 12000;

// Recent speech answers "who were we just talking to?"; the backend's persistent
// visit summary carries the longer-lived identity signal after this ages out.
export const CONTEXT_MAX_AGE_MS = 15 * 60 * 1000;

const LOG = "[MR++] [Context]";

export class MultiRecordContext {
  constructor({
    charCap = CONTEXT_CHAR_CAP,
    maxAgeMs = CONTEXT_MAX_AGE_MS,
  } = {}) {
    this.charCap = charCap;
    this.maxAgeMs = maxAgeMs;
    // [{ conversationId, text, at }], oldest first.
    this.entries = [];
  }

  /**
   * Accumulate what the backend just attributed. Junk is dropped rather than
   * stored: a bad entry would go straight back out in the next payload.
   */
  append(utterances, at = Date.now()) {
    if (!Array.isArray(utterances)) return;
    for (const utterance of utterances) {
      if (!utterance || typeof utterance !== "object") continue;
      const conversationId = String(utterance.conversationId ?? "").trim();
      const text = String(utterance.text ?? "").trim();
      if (!conversationId || !text) continue;
      this.entries.push({ conversationId, text, at });
    }
    this.prune(at);
  }

  /** Drop what is too old, then what does not fit. */
  prune(now = Date.now()) {
    const beforeChars = this.entries.reduce(
      (total, entry) => total + entry.text.length,
      0,
    );
    const fresh = this.entries.filter(
      (entry) => now - entry.at <= this.maxAgeMs,
    );
    const freshChars = fresh.reduce(
      (total, entry) => total + entry.text.length,
      0,
    );

    const kept = [];
    let total = 0;
    for (let i = fresh.length - 1; i >= 0; i -= 1) {
      const entry = fresh[i];
      const remaining = this.charCap - total;
      if (remaining <= 0) break;
      if (entry.text.length > remaining) {
        // Keep the newest part of a lone oversized utterance rather than letting
        // one entry bypass the request-size cap. Older entries stay whole.
        if (!kept.length) {
          kept.unshift({ ...entry, text: entry.text.slice(-remaining) });
        }
        break;
      }
      total += entry.text.length;
      kept.unshift(entry);
    }
    this.entries = kept;

    const keptChars = kept.reduce(
      (total, entry) => total + entry.text.length,
      0,
    );
    if (keptChars < beforeChars) {
      console.log(
        `${LOG} truncated context: before=${beforeChars} chars, after=${keptChars} chars, age-dropped=${beforeChars - freshChars}, size-dropped=${freshChars - keptChars}, cap=${this.charCap}`,
      );
    }
  }

  /**
   * The wire shape the segmenter endpoint takes, for ONE chunk. Timestamps stay
   * local.
   *
   * `targetIds` is that chunk's frozen target set, and the returned context is
   * filtered to it. Retention is deliberately wider than transmission: a visit
   * the clinician steps away from stays in `entries` so resuming it still has an
   * anchor, but its transcript is not shipped in a request about other patients.
   * Sending it would be one patient's text in another's request, and it can only
   * mislead — the segmenter constrains its output to the chunk's targets, so an
   * unrelated visit cannot receive the text it might otherwise pull.
   *
   * Fails CLOSED: no targets means no context, rather than sending everything.
   */
  payload(now = Date.now(), targetIds = []) {
    // Prune on the way out too, not just on append: between a long gap and the
    // next successful upload nothing appends, so the age cap would otherwise
    // ship a visit that ended well outside the window.
    this.prune(now);
    const targets = new Set((targetIds || []).map(String));
    if (!targets.size) return [];
    return this.entries
      .filter((entry) => targets.has(entry.conversationId))
      .map(({ conversationId, text }) => ({ conversationId, text }));
  }

  get size() {
    return this.entries.length;
  }

  reset() {
    this.entries = [];
  }

  toJSON() {
    return { entries: this.entries };
  }

  /** Rebuild from a snapshot, dropping anything that aged out while away. */
  static from(snapshot, options = {}) {
    const context = new MultiRecordContext(options);
    const entries = snapshot && snapshot.entries;
    if (Array.isArray(entries)) {
      for (const entry of entries) {
        if (!entry || typeof entry !== "object") continue;
        const conversationId = String(entry.conversationId ?? "").trim();
        const text = String(entry.text ?? "").trim();
        const at = Number(entry.at);
        if (!conversationId || !text || !Number.isFinite(at)) continue;
        context.entries.push({ conversationId, text, at });
      }
      context.entries.sort((a, b) => a.at - b.at);
      context.prune();
    }
    return context;
  }
}

export default MultiRecordContext;
